// CSR-1606: Commission by Line of Business section for the Insurance
// Company detail page (carrier / MGA only — parents carry no commission).
// Mirrors the lodestar prototype: a list of per-line overrides of the
// carrier's general commission, with an add row (Line of Business dropdown
// sourced from the policy types present in the system + Rate %) and a
// delete action per row. The matching rate pre-fills Commission (%) when a
// policy is created for this carrier.

import { useMemo, useState } from "react";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { SearchableSelect } from "@/components/searchable-select";
import { CircleDollarSign, Loader2, Trash2 } from "lucide-react";
import {
  useCreateInsuranceCompanyCommissionRate,
  useDeleteInsuranceCompanyCommissionRate,
  useInsuranceCompanyCommissionRates,
  usePolicyTypes,
} from "@/lib/api";
import { useToast } from "@/hooks/use-toast";
import { cn } from "@/lib/utils";
import { DetailSectionHeader } from "./DetailSectionHeader";

function formatRate(value: string): string {
  const num = Number(value);
  if (!Number.isFinite(num)) return `${value}%`;
  return `${num % 1 === 0 ? num.toFixed(0) : num.toFixed(2)}%`;
}

function isRateInRange(value: string): boolean {
  const num = Number(value);
  return Number.isFinite(num) && num >= 0 && num <= 100;
}

interface CommissionRatesSectionProps {
  companyId: string;
}

export function CommissionRatesSection({ companyId }: CommissionRatesSectionProps) {
  const { data: rates, isLoading, error } = useInsuranceCompanyCommissionRates(companyId);
  const { data: policyTypes } = usePolicyTypes();
  const createRate = useCreateInsuranceCompanyCommissionRate();
  const deleteRate = useDeleteInsuranceCompanyCommissionRate();
  const { toast } = useToast();

  const [lineOfBusiness, setLineOfBusiness] = useState("");
  const [ratePct, setRatePct] = useState("");
  const [rateTouched, setRateTouched] = useState(false);

  // A line can carry only one override — keep already-used lines out of the
  // dropdown instead of letting the POST 409.
  const availableTypes = useMemo(() => {
    const used = new Set(
      (rates ?? []).map((rate) => rate.lineOfBusiness.trim().toLowerCase()),
    );
    return (policyTypes ?? []).filter(
      (type) => !used.has(type.trim().toLowerCase()),
    );
  }, [policyTypes, rates]);

  const rateInvalid = rateTouched && ratePct !== "" && !isRateInRange(ratePct);
  const canAdd =
    !createRate.isPending &&
    lineOfBusiness.trim() !== "" &&
    ratePct.trim() !== "" &&
    isRateInRange(ratePct);

  const handleAdd = () => {
    if (!canAdd) return;
    createRate.mutate(
      { companyId, data: { lineOfBusiness, ratePct: ratePct.trim() } },
      {
        onSuccess: () => {
          setLineOfBusiness("");
          setRatePct("");
          setRateTouched(false);
        },
        onError: (err: unknown) => {
          toast({
            title: "Failed to add commission rate",
            description: err instanceof Error ? err.message : undefined,
            variant: "destructive",
          });
        },
      },
    );
  };

  const handleDelete = (rateId: string) => {
    deleteRate.mutate(
      { id: rateId, companyId },
      {
        onError: (err: unknown) => {
          toast({
            title: "Failed to delete commission rate",
            description: err instanceof Error ? err.message : undefined,
            variant: "destructive",
          });
        },
      },
    );
  };

  return (
    <section className="flex flex-col gap-3" data-testid="section-commission-rates">
      <Card className="overflow-hidden p-0">
        <DetailSectionHeader
          icon={<CircleDollarSign className="w-5 h-5 text-primary" />}
          title="Commission by Line of Business"
          count={rates && rates.length > 0 ? rates.length : undefined}
          testid="commission-rates-total"
        />
        <div className="px-6 py-4 flex flex-col gap-4">
          <p className="text-sm text-muted-foreground">
            Specific rates override the general commission for policies of that
            line of business. The matching rate is applied to a policy when it
            is created.
          </p>

          {error ? (
            <p className="text-sm text-destructive" data-testid="commission-rates-error">
              Failed to load commission rates.
            </p>
          ) : isLoading || !rates ? (
            <div className="flex items-center gap-2 text-sm text-muted-foreground">
              <Loader2 className="w-4 h-4 animate-spin" />
              Loading commission rates…
            </div>
          ) : rates.length === 0 ? (
            <p
              className="text-sm text-muted-foreground italic"
              data-testid="commission-rates-empty"
            >
              No line-specific rates yet — the general commission applies to
              every policy.
            </p>
          ) : (
            <div className="flex flex-col gap-2">
              {rates.map((rate) => (
                <div
                  key={rate.id}
                  className="flex items-center justify-between rounded-xl border border-border px-4 py-3"
                  data-testid={`commission-rate-row-${rate.id}`}
                >
                  <span className="font-medium text-foreground">
                    {rate.lineOfBusiness}
                  </span>
                  <span className="flex items-center gap-3">
                    <span
                      className="font-semibold text-amber-600"
                      data-testid={`commission-rate-value-${rate.id}`}
                    >
                      {formatRate(rate.ratePct)}
                    </span>
                    <Button
                      variant="ghost"
                      size="icon"
                      className="cursor-pointer text-muted-foreground hover:text-destructive"
                      onClick={() => handleDelete(rate.id)}
                      disabled={deleteRate.isPending}
                      aria-label={`Delete ${rate.lineOfBusiness} commission rate`}
                      data-testid={`commission-rate-delete-${rate.id}`}
                    >
                      <Trash2 className="w-4 h-4" />
                    </Button>
                  </span>
                </div>
              ))}
            </div>
          )}

          <div className="flex flex-col sm:flex-row gap-3 sm:items-end">
            <div className="flex-1 min-w-0">
              <Label htmlFor="commission-rate-lob">Line of Business</Label>
              <SearchableSelect
                id="commission-rate-lob"
                value={lineOfBusiness}
                onChange={setLineOfBusiness}
                options={availableTypes.map((type) => ({
                  value: type,
                  label: type,
                }))}
                placeholder="Select…"
              />
            </div>
            <div className="w-full sm:w-32">
              <Label htmlFor="commission-rate-pct">Rate (%)</Label>
              <Input
                id="commission-rate-pct"
                data-testid="commission-rate-pct"
                inputMode="decimal"
                placeholder="0"
                value={ratePct}
                onChange={(e) => setRatePct(e.target.value)}
                onBlur={() => setRateTouched(true)}
                className={cn(rateInvalid && "border-destructive")}
              />
              {rateInvalid && (
                <p className="text-xs text-destructive mt-1">
                  Must be between 0 and 100
                </p>
              )}
            </div>
            <Button
              onClick={handleAdd}
              disabled={!canAdd}
              className="cursor-pointer"
              data-testid="commission-rate-add"
            >
              {createRate.isPending ? (
                <Loader2 className="w-4 h-4 animate-spin" />
              ) : (
                "Add"
              )}
            </Button>
          </div>
        </div>
      </Card>
    </section>
  );
}
